// /src/ui/components/FileMenu.tsx

import React, { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import {
  IconChevronDown,
  IconExport,
  IconFile,
  IconFolderOpen,
  IconImport,
  IconPlus,
  IconSave,
} from '@/ui/icons';

type FileMenuProps = {
  canExport?: boolean;
  onNew: () => void;
  onImportLDraw: () => void;
  onExportLDraw: () => void;
  onSaveJson: () => void;
  onLoadJson: () => void;
};

export default function FileMenu({
  canExport = true,
  onNew,
  onImportLDraw,
  onExportLDraw,
  onSaveJson,
  onLoadJson,
}: FileMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;

    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (!t) return;
      if (rootRef.current && rootRef.current.contains(t)) return;
      setOpen(false);
    };

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };

    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const act = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <div className="menu" ref={rootRef}>
      <button
        className={clsx('btn', 'menuButton', open && 'active')}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="File"
        type="button"
      >
        <IconFile className="ico" />
        <span className="menuButtonLabel">File</span>
        <IconChevronDown className="ico chev" />
      </button>

      {open && (
        <div className="menuPopover" role="menu" aria-label="File menu">
          <button
            className="menuItem"
            role="menuitem"
            onClick={() => act(onNew)}
            type="button"
            title="Start a new model (clears the scene)"
          >
            <IconPlus className="ico" />
            <span>New</span>
          </button>

          <div className="menuSep" />

          <button
            className="menuItem"
            role="menuitem"
            onClick={() => act(onImportLDraw)}
            type="button"
            title="Import LDraw (.ldr / .mpd)"
          >
            <IconImport className="ico" />
            <span>Import LDraw…</span>
          </button>

          <button
            className="menuItem"
            role="menuitem"
            onClick={() => act(onExportLDraw)}
            type="button"
            disabled={!canExport}
            title={canExport ? 'Export MPD (.mpd)' : 'Nothing to export'}
          >
            <IconExport className="ico" />
            <span>Export LDraw…</span>
          </button>

          <div className="menuSep" />

          <button
            className="menuItem"
            role="menuitem"
            onClick={() => act(onSaveJson)}
            type="button"
            title="Save editor snapshot (.json)"
          >
            <IconSave className="ico" />
            <span>Save JSON…</span>
          </button>

          <button
            className="menuItem"
            role="menuitem"
            onClick={() => act(onLoadJson)}
            type="button"
            title="Load editor snapshot (.json)"
          >
            <IconFolderOpen className="ico" />
            <span>Load JSON…</span>
          </button>
        </div>
      )}
    </div>
  );
}
